<script setup lang="ts">
import { InfoCircleIcon } from '@gohighlevel/ghl-icons/24/outline';
import { UIAlert, UITooltip } from '@gohighlevel/ghl-ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    id?: string
    alertClass?: string
    tooltipPlacement?: string
    titleKey?: string
    messageKey?: string
    learnMoreKey?: string
    tooltipTitleKey?: string
    tooltipContentKey?: string
    useDefaultStyles?: boolean
  }>(),
  {
    alertClass: '',
    tooltipPlacement: 'top-start',
    titleKey: 'settings.calendar.event_booking_alert_title',
    messageKey: 'settings.calendar.event_booking_alert_message',
    learnMoreKey: 'settings.calendar.event_booking_learn_more',
    tooltipTitleKey: 'settings.calendar.event_booking_tooltip_title',
    tooltipContentKey: 'settings.calendar.event_booking_tooltip_content',
    useDefaultStyles: true,
  }
)

const { t } = useI18n()

const alertClasses = computed(() => {
  const classes = ['ghl-event-calendar-alert']

  if (props.useDefaultStyles) {
    classes.push('w-full', 'bg-gray-50/70', 'py-0.5', 'text-xs')
  }

  if (props.alertClass) {
    classes.push(props.alertClass)
  }

  return classes
})

</script>

<template>
  <UIAlert
    :id="id"
    type="default"
    :closable="false"
    :class="alertClasses"
  >
    <template #icon></template>
    <template #content>
      <slot>
        <div class="flex w-full flex-wrap items-center justify-between gap-2">
          <div class="flex flex-1 items-center gap-1 text-gray-600">
            <span class="font-semibold text-gray-700 text-sm">
              {{ t(titleKey) }}:
            </span>
            <span class="text-sm">
              {{ t(messageKey) }}
            </span>
          </div>
          <UITooltip :placement="tooltipPlacement" trigger="hover">
            <template #trigger>
              <div class="flex items-center gap-1 whitespace-nowrap cursor-help text-gray-700">
                <InfoCircleIcon class="h-5 w-5" />
                <span class="text-sm font-medium">
                  {{ t(learnMoreKey) }}
                </span>
              </div>
            </template>
            <div class="text-sm">
              <div class="mb-2 font-medium">
                {{ t(tooltipTitleKey) }}
              </div>
              <div class="space-y-1 whitespace-pre-line">
                {{ t(tooltipContentKey) }}
              </div>
            </div>
          </UITooltip>
        </div>
      </slot>
    </template>
  </UIAlert>
</template>

<style scoped>

:deep(.n-alert__body),
:deep(.n-alert-body) {
  padding: 0.75rem 2.5rem !important;
}

:deep(.n-alert-body__content),
:deep(.body) {
  margin-right: 0 !important;
}

</style>

